import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import X from 'lucide-react/dist/esm/icons/x.js';
import Image from 'lucide-react/dist/esm/icons/image.js';
import Link2 from 'lucide-react/dist/esm/icons/link-2.js';
import LayoutGrid from 'lucide-react/dist/esm/icons/layout-grid.js';
import LayoutList from 'lucide-react/dist/esm/icons/layout-list.js';
import Maximize2 from 'lucide-react/dist/esm/icons/maximize-2.js';
import Search from 'lucide-react/dist/esm/icons/search.js';
import Check from 'lucide-react/dist/esm/icons/check.js';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw.js';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Upload from 'lucide-react/dist/esm/icons/upload.js';
import FileImage from 'lucide-react/dist/esm/icons/file-image.js';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2.js';
import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2.js';
import XCircle from 'lucide-react/dist/esm/icons/x-circle.js';
import api from '../api';
import { useTranslations } from '../contexts/TranslationContext';

const RECENT_KEY = 'tlf_editor_recent_images';
const VIEW_KEY   = 'tlf_editor_image_view';

function getRecentImages() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}
function saveRecentImage(url) {
  const prev    = getRecentImages();
  const updated = [url, ...prev.filter(u => u !== url)].slice(0, 24);
  localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
}

/* ─── tiny helpers ─── */
const readableSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024)       return `${bytes} B`;
  if (bytes < 1024 ** 2)  return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
};

const ACCEPTED = ['image/jpeg','image/png','image/gif','image/webp','image/svg+xml','image/avif'];
const PAGE_SIZE = 36;

/* ─────────────────────────────────────────────────
   Upload queue item shape:
   { id, file, preview, status: 'pending'|'uploading'|'done'|'error',
     progress: 0-100, resultUrl: string|null, error: string|null }
───────────────────────────────────────────────── */

export default function ImagePickerModal({ onSelect, onClose }) {
  const { t } = useTranslations();
  const [view,         setView]         = useState(() => localStorage.getItem(VIEW_KEY) || 'tile');
  const [tab,          setTab]          = useState('library');
  const [search,       setSearch]       = useState('');
  const [url,          setUrl]          = useState('');
  const [urlError,     setUrlError]     = useState(false);
  const [images,       setImages]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [recentImages, setRecentImages] = useState([]);
  const [queue,        setQueue]        = useState([]);   // upload queue
  const [dragging,     setDragging]     = useState(false);
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE);

  const urlInputRef  = useRef(null);
  const fileInputRef = useRef(null);
  const loadMoreRef  = useRef(null);
  const dragCounter  = useRef(0);

  /* ── Library data ── */
  const recentAsItems = getRecentImages().map(u => ({
    url: u, name: u.split('/').pop().split('?')[0] || 'image', isRecent: true,
  }));
  const libraryItems = images.length > 0 ? images : recentAsItems;
  const filtered = libraryItems.filter(img =>
    !search ||
    (img.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (img.url  || '').toLowerCase().includes(search.toLowerCase())
  );
  const visibleFiltered = filtered.slice(0, displayLimit);

  useEffect(() => {
    setRecentImages(getRecentImages());
    fetchImages();
  }, []);

  useEffect(() => {
    if (tab === 'url') setTimeout(() => urlInputRef.current?.focus(), 60);
  }, [tab]);

  /* Reset pagination on tab/search/view/image updates */
  useEffect(() => {
    setDisplayLimit(PAGE_SIZE);
  }, [search, tab, view, images]);

  /* Infinite scroll observer */
  useEffect(() => {
    if (tab !== 'library') return;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setDisplayLimit(prev => Math.min(prev + PAGE_SIZE, filtered.length));
      }
    }, { threshold: 0.1 });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [tab, filtered.length, displayLimit]);

  /* cleanup object URLs */
  useEffect(() => () => {
    queue.forEach(q => q.preview && URL.revokeObjectURL(q.preview));
  }, []);

  /* ── API ── */
  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/organization/assets');
      const payload = res.data?.payload ?? res.data;
      const mapped = (Array.isArray(payload) ? payload : []).map(item => ({
        url:  item.cdn_location || item.url,
        name: item.fileName || item.name || '',
        size: typeof item.size === 'number' ? readableSize(item.size) : (item.size || ''),
        // sizes array from API: [{ width, cdn_location, size }] — already grouped, variants excluded
        sizes: Array.isArray(item.sizes) ? item.sizes : [],
      }));
      setImages(mapped);
    } catch {
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  /* ── Insert / select ── */
  const handleInsert = (imageUrl, imageSizes) => {
    saveRecentImage(imageUrl);
    if (onSelect) {
      onSelect(imageUrl, imageSizes ?? []);
      onClose();
    }
  };

  /* ── View preference ── */
  const changeView = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };

  /* ── Upload helpers ── */
  const addFiles = useCallback((files) => {
    const valid = Array.from(files).filter(f => ACCEPTED.includes(f.type));
    if (!valid.length) return;
    const items = valid.map(file => ({
      id:        crypto.randomUUID(),
      file,
      preview:   URL.createObjectURL(file),
      status:    'pending',
      progress:  0,
      resultUrl: null,
      error:     null,
    }));
    setQueue(prev => [...prev, ...items]);
  }, []);

  const uploadFile = async (item) => {
    setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'uploading', progress: 0 } : q));

    const form = new FormData();
    form.append('file', item.file);

    try {
      const res = await api.post('/admin/organization/assets', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded / e.total) * 100);
          setQueue(prev => prev.map(q => q.id === item.id ? { ...q, progress: pct } : q));
        },
      });

      // API returns OperationResult<List<BlobPostDataResponse>>
      const payload = res.data?.payload ?? res.data;
      const first = Array.isArray(payload) ? payload[0] : payload;
      const resultUrl = first?.cDNPath || first?.cdnPath || first?.CDNPath || first?.absolutePath || null;

      setQueue(prev => prev.map(q =>
        q.id === item.id ? { ...q, status: 'done', progress: 100, resultUrl } : q
      ));
      // Re-fetch library to get the new image in the grid
      await fetchImages();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Upload failed';
      setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'error', error: msg } : q));
    }
  };

  const uploadAll = () => {
    queue.filter(q => q.status === 'pending').forEach(uploadFile);
  };

  const removeFromQueue = (id) => {
    setQueue(prev => {
      const item = prev.find(q => q.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter(q => q.id !== id);
    });
  };

  /* ── Drag & drop ── */
  const onDragEnter = (e) => { e.preventDefault(); dragCounter.current++; setDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); dragCounter.current--; if (dragCounter.current === 0) setDragging(false); };
  const onDragOver  = (e) => e.preventDefault();
  const onDrop      = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const pendingCount   = queue.filter(q => q.status === 'pending').length;
  const uploadingCount = queue.filter(q => q.status === 'uploading').length;
  const doneCount      = queue.filter(q => q.status === 'done').length;
  const hasQueue       = queue.length > 0;

  /* ════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════ */
  const content = (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-[860px] max-w-[96vw] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-2 border-blue-500 flex flex-col overflow-hidden"
        style={{ height: '82vh' }}
        onClick={e => e.stopPropagation()}
      >

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 flex-shrink-0">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Image className="w-5 h-5" />
            {t('imageLibrary.title')}
          </h3>
          <button onClick={onClose} className="text-blue-200 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/20">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 flex-wrap">

          {/* Tab pills */}
          <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-0.5 shadow-sm">
            {[
              { id: 'library', icon: Image,  label: t('imageLibrary.tabs.library') },
              { id: 'upload',  icon: Upload, label: t('imageLibrary.tabs.upload') },
              { id: 'url',     icon: Link2,  label: t('imageLibrary.tabs.customUrl') },
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  tab === id
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                {id === 'upload' && queue.length > 0 && (
                  <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] leading-none ${
                    tab === 'upload' ? 'bg-white/30 text-white' : 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  }`}>
                    {queue.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {tab === 'library' && (
            <>
              <div className="flex-1 relative min-w-[140px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={t('imageLibrary.searchPlaceholder')}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>
              <div className="flex items-center gap-0.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-0.5 shadow-sm">
                {[
                  { id: 'tile',  icon: LayoutGrid, label: 'Tile' },
                  { id: 'list',  icon: LayoutList, label: 'List' },
                  { id: 'large', icon: Maximize2,  label: 'Large' },
                ].map(({ id, icon: Icon, label }) => (
                  <button key={id} onClick={() => changeView(id)} title={`${label} view`}
                    className={`p-1.5 rounded transition-all ${view === id ? 'bg-blue-600 text-white shadow' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </button>
                ))}
              </div>
              <button onClick={fetchImages} title="Refresh" className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-blue-500' : ''}`} />
              </button>
            </>
          )}

          {tab === 'upload' && hasQueue && (
            <div className="flex items-center gap-2 ml-auto">
              {pendingCount > 0 && (
                <button
                  onClick={uploadAll}
                  disabled={uploadingCount > 0}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {t('imageLibrary.tabs.upload')} {pendingCount} file{pendingCount !== 1 ? 's' : ''}
                </button>
              )}
              {doneCount > 0 && (
                <span className="text-xs text-green-600 dark:text-green-400 font-semibold flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {doneCount} {t('imageLibrary.upload.uploaded')}
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto scroll-smooth scrollbar-thin">

          {/* ─── Upload tab ─── */}
          {tab === 'upload' && (
            <div className="p-5 flex flex-col gap-4">

              {/* Drop zone */}
              <div
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed cursor-pointer transition-all min-h-[180px] select-none ${
                  dragging
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 scale-[1.01]'
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/40 bg-white dark:bg-gray-800/50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED.join(',')}
                  className="hidden"
                  onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
                />
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${
                  dragging ? 'bg-blue-100 dark:bg-blue-900/60' : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  <Upload className={`w-7 h-7 transition-colors ${dragging ? 'text-blue-500' : 'text-gray-400'}`} />
                </div>
                <div className="text-center">
                  <p className={`text-sm font-semibold ${dragging ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                    {dragging ? t('imageLibrary.upload.dropToAdd') : t('imageLibrary.upload.dragDrop')}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    or <span className="text-blue-500 font-medium">{t('imageLibrary.upload.clickToBrowse')}</span> · JPG, PNG, GIF, WebP, SVG, AVIF
                  </p>
                </div>
              </div>

              {/* Queue */}
              {hasQueue && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {t('imageLibrary.upload.queue')} — {queue.length} file{queue.length !== 1 ? 's' : ''}
                  </p>
                  <div className="space-y-2 pr-1">
                    {queue.map(item => (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${
                          item.status === 'done'      ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20' :
                          item.status === 'error'     ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20' :
                          item.status === 'uploading' ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20' :
                                                        'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                        }`}
                      >
                        {/* Thumbnail */}
                        <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 relative">
                          <img src={item.preview} aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-sm opacity-40" />
                          <img src={item.preview} alt={item.file.name} className="absolute inset-0 w-full h-full object-contain" />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.file.name}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{readableSize(item.file.size)}</p>

                          {/* Progress bar */}
                          {item.status === 'uploading' && (
                            <div className="mt-1.5 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full transition-all duration-200"
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                          )}

                          {item.status === 'error' && (
                            <p className="text-xs text-red-500 mt-0.5 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> {item.error}
                            </p>
                          )}
                        </div>

                        {/* Status / actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {item.status === 'done' && item.resultUrl && (
                            <button
                              onClick={() => handleInsert(item.resultUrl)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
                            >
                              <Check className="w-3.5 h-3.5" /> {t('imageLibrary.insert')}
                            </button>
                          )}
                          {item.status === 'done' && (
                            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                          )}
                          {item.status === 'error' && (
                            <button
                              onClick={() => uploadFile(item)}
                              className="px-2.5 py-1 text-xs font-semibold text-red-600 dark:text-red-400 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                            >
                              {t('imageLibrary.upload.retry')}
                            </button>
                          )}
                          {item.status === 'uploading' && (
                            <span className="text-xs text-blue-500 font-semibold tabular-nums">{item.progress}%</span>
                          )}
                          {item.status === 'pending' && (
                            <button
                              onClick={() => uploadFile(item)}
                              className="px-3 py-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                            >
                              {t('imageLibrary.upload.button')}
                            </button>
                          )}
                          <button onClick={() => removeFromQueue(item.id)} className="text-gray-300 hover:text-red-500 dark:hover:text-red-400 transition-colors p-0.5" title="Remove">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── Custom URL tab ─── */}
          {tab === 'url' && (
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  {t('imageLibrary.url.label')}
                </label>
                <div className="flex gap-2">
                  <input
                    ref={urlInputRef}
                    type="url"
                    value={url}
                    onChange={e => { setUrl(e.target.value); setUrlError(false); }}
                    onKeyDown={e => e.key === 'Enter' && url.trim() && handleInsert(url.trim())}
                    placeholder="https://cdn.tlffs.ca/images/banner.jpg"
                    className="flex-1 px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800"
                    autoFocus
                  />
                  <button
                    onClick={() => url.trim() && handleInsert(url.trim())}
                    disabled={!url.trim()}
                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
                  >
                    {t('imageLibrary.insert')}
                  </button>
                </div>
              </div>
              {url && (
                <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden bg-gray-50 dark:bg-gray-900">
                  <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{t('imageLibrary.preview')}</div>
                  <div className="p-4 flex justify-center">
                    <img src={url} alt="preview" className="max-h-52 max-w-full rounded-lg object-contain shadow"
                      onError={() => setUrlError(true)} onLoad={() => setUrlError(false)} />
                  </div>
                  {urlError && (
                    <div className="flex items-center gap-1.5 text-xs text-red-500 justify-center pb-3">
                      <AlertCircle className="w-3.5 h-3.5" /> {t('imageLibrary.url.loadError')}
                    </div>
                  )}
                </div>
              )}
              {recentImages.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                    <Clock className="w-3.5 h-3.5" /> {t('imageLibrary.recentlyUsed')}
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    {recentImages.slice(0, 12).map((imgUrl, i) => (
                      <button key={i} onClick={() => handleInsert(imgUrl)} title={imgUrl}
                        className="relative aspect-square rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 overflow-hidden bg-gray-100 dark:bg-gray-700 transition-all group">
                        <img src={imgUrl} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── Library tab ─── */}
          {tab === 'library' && (
            loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('imageLibrary.loading')}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-8 text-center gap-3">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <Image className="w-8 h-8 text-gray-300 dark:text-gray-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                    {search ? `${t('imageLibrary.empty.searchTitle')} "${search}"` : t('imageLibrary.empty.title')}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {search ? t('imageLibrary.empty.searchHint') : t('imageLibrary.empty.hint')}
                  </p>
                </div>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => { setSearch(''); setTab('upload'); }}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                    <Upload className="w-3.5 h-3.5" /> {t('imageLibrary.empty.uploadButton')}
                  </button>
                  <button onClick={() => { setSearch(''); setTab('url'); }}
                    className="px-4 py-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors">
                    {t('imageLibrary.empty.urlButton')}
                  </button>
                </div>
              </div>
            ) : view === 'tile' ? (
              <div className="p-4">
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2.5">
                  {visibleFiltered.map((img, i) => (
                    <button key={i} onClick={() => handleInsert(img.url, img.sizes)} title={img.name}
                      className="group relative aspect-square rounded-xl overflow-hidden border-2 border-transparent hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all shadow-sm hover:shadow-md bg-gray-100 dark:bg-gray-700">
                      {/* Blurred backdrop fills the tile; foreground shows full image */}
                      <img src={img.url} aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-sm opacity-40" loading="lazy" />
                      <img src={img.url} alt={img.name} className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-1.5">
                        <p className="text-white text-[10px] font-medium truncate">{img.name}</p>
                      </div>
                      <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      </div>
                      {img.isRecent && <div className="absolute top-1.5 left-1.5 w-1.5 h-1.5 bg-amber-400 rounded-full" title="Recently used" />}
                      {img.sizes?.length > 0 && (
                        <div className="absolute bottom-1.5 left-1.5 px-1 py-0.5 bg-emerald-600/90 text-white text-[9px] font-bold rounded leading-none" title="Responsive sizes available">
                          {img.sizes.length} sizes
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                {filtered.length > displayLimit && (
                  <div ref={loadMoreRef} className="py-6 flex flex-col items-center justify-center gap-2">
                    <button
                      onClick={() => setDisplayLimit(prev => Math.min(prev + PAGE_SIZE, filtered.length))}
                      className="px-5 py-2 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-300 font-semibold text-xs rounded-xl transition-all shadow-sm"
                    >
                      Load More ({filtered.length - displayLimit} remaining)
                    </button>
                    <span className="text-[11px] text-gray-400">
                      Showing {visibleFiltered.length} of {filtered.length} images
                    </span>
                  </div>
                )}
              </div>
            ) : view === 'list' ? (
              <div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                  {visibleFiltered.map((img, i) => (
                    <button key={i} onClick={() => handleInsert(img.url, img.sizes)}
                      className="w-full flex items-center gap-4 px-5 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left group">
                      <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 shadow-sm relative">
                        <img src={img.url} aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-sm opacity-40" loading="lazy" />
                        <img src={img.url} alt={img.name} className="absolute inset-0 w-full h-full object-contain" loading="lazy" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {img.name}
                          {img.isRecent && <span className="ml-2 text-[10px] font-normal px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">{t('imageLibrary.recent')}</span>}
                          {img.sizes?.length > 0 && <span className="ml-2 text-[10px] font-normal px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded">{img.sizes.length} sizes</span>}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{img.url}</p>
                        {img.size && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{img.size}</p>}
                      </div>
                      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all translate-x-1 group-hover:translate-x-0">
                        <span className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg shadow">{t('imageLibrary.insert')}</span>
                      </div>
                    </button>
                  ))}
                </div>
                {filtered.length > displayLimit && (
                  <div ref={loadMoreRef} className="py-6 flex flex-col items-center justify-center gap-2">
                    <button
                      onClick={() => setDisplayLimit(prev => Math.min(prev + PAGE_SIZE, filtered.length))}
                      className="px-5 py-2 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-300 font-semibold text-xs rounded-xl transition-all shadow-sm"
                    >
                      Load More ({filtered.length - displayLimit} remaining)
                    </button>
                    <span className="text-[11px] text-gray-400">
                      Showing {visibleFiltered.length} of {filtered.length} images
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {visibleFiltered.map((img, i) => (
                    <button key={i} onClick={() => handleInsert(img.url, img.sizes)}
                      className="group relative rounded-2xl overflow-hidden border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all shadow hover:shadow-lg">
                      <div className="aspect-video bg-gray-100 dark:bg-gray-700 overflow-hidden relative">
                        <img src={img.url} aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-sm opacity-40" loading="lazy" />
                        <img src={img.url} alt={img.name} className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      </div>
                      <div className="px-3 py-2.5 bg-white dark:bg-gray-800 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{img.name}</p>
                            {img.isRecent && <p className="text-[10px] text-amber-500 dark:text-amber-400 mt-0.5">{t('imageLibrary.recentlyUsed')}</p>}
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center shadow">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                {filtered.length > displayLimit && (
                  <div ref={loadMoreRef} className="py-6 flex flex-col items-center justify-center gap-2">
                    <button
                      onClick={() => setDisplayLimit(prev => Math.min(prev + PAGE_SIZE, filtered.length))}
                      className="px-5 py-2 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-300 font-semibold text-xs rounded-xl transition-all shadow-sm"
                    >
                      Load More ({filtered.length - displayLimit} remaining)
                    </button>
                    <span className="text-[11px] text-gray-400">
                      Showing {visibleFiltered.length} of {filtered.length} images
                    </span>
                  </div>
                )}
              </div>
            )
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-5 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
          {tab === 'library' && !loading
            ? <span>{filtered.length} {filtered.length === 1 ? t('imageLibrary.footer.image') : t('imageLibrary.footer.images')}{images.length === 0 && recentImages.length > 0 ? ` ${t('imageLibrary.footer.recentlyUsedSuffix')}` : ''}{search ? ` ${t('imageLibrary.footer.matching')} "${search}"` : ''}</span>
            : tab === 'upload'
            ? <span>{queue.length} file{queue.length !== 1 ? 's' : ''} {t('imageLibrary.footer.inQueue')}{doneCount > 0 ? ` · ${doneCount} ${t('imageLibrary.upload.uploaded')}` : ''}</span>
            : <span />
          }
          <div className="flex gap-3">
            {tab !== 'upload'  && <button onClick={() => setTab('upload')}  className="text-blue-500 dark:text-blue-400 hover:underline font-medium flex items-center gap-1"><Upload className="w-3 h-3" /> {t('imageLibrary.tabs.upload')}</button>}
            {tab !== 'library' && <button onClick={() => setTab('library')} className="text-blue-500 dark:text-blue-400 hover:underline font-medium">{t('imageLibrary.backToLibrary')}</button>}
            {tab !== 'url'     && <button onClick={() => setTab('url')}     className="text-blue-500 dark:text-blue-400 hover:underline font-medium">{t('imageLibrary.addCustomUrl')}</button>}
          </div>
        </div>

      </div>
    </div>
  );

  return createPortal(content, document.body);
}
